import React, { useState } from 'react';
import { Lock, RefreshCw } from 'react-feather';
import { Header } from '../Header/Header';
import { authService } from '../../services/api/authService';
import { billingService } from '../../services/api/billingService';
import { storage } from '../../utils/storage';
import { ROUTES } from '../../constants/routes';
import { useI18n } from '../../i18n';
import './SubscriptionPendingBlock.scss';

/**
 * Full-screen block shown to a medical officer whose clinic has no active
 * subscription. Invited doctors can finish account setup (password + MFA) and
 * log in, but land here until their clinic subscribes — the matching EHR API
 * gate denies data access server-side, so this is not merely cosmetic.
 *
 * Rendered by `ProtectedRoute` (see App.tsx) for any doctor route while
 * `isSubscriptionActive()` is false. Self-contained (its own logout) so it can
 * be rendered generically without threading `onLogout` from every route.
 */
export function SubscriptionPendingBlock() {
  const { t } = useI18n();
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState('');

  const currentUser = storage.getAuthData()?.user;

  const handleLogout = async () => {
    try {
      // authService.logout() clears all stored auth/session data.
      await authService.logout();
    } catch {
      storage.clearAll();
    } finally {
      window.location.assign(ROUTES.LOGIN);
    }
  };

  // Once the clinic admin subscribes, the doctor can re-check without logging
  // out: this forces a token refresh so the new `subscription_active`
  // entitlement applies. The doctor is typically not the clinic owner, so this
  // only re-checks status — it does not start a subscription.
  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshError('');
    try {
      const ok = await billingService.refreshEntitlements();
      if (ok) {
        // Reload so ProtectedRoute re-evaluates with the refreshed entitlements.
        window.location.assign(ROUTES.DOCTOR_DASHBOARD);
        return;
      }
      setRefreshError(t('subscription.stillPending'));
    } catch {
      setRefreshError(t('subscription.checkFailed'));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div data-component="SubscriptionPendingBlock" className="subscription-pending-page">
      <Header onLogout={handleLogout} pageType="dashboard" />

      <main className="subscription-pending-main">
        <div className="subscription-pending-blocker">
          <div className="blocker-icon">
            <Lock size={64} />
          </div>
          <h2>{t('subscription.title')}</h2>
          <p>
            {currentUser?.name
              ? t('subscription.bodyNamed', { name: currentUser.name })
              : t('subscription.bodyUnnamed')}
          </p>
          <p>
            {t('subscription.access')}
          </p>
          <p className="help-text">
            {t('subscription.help')}
          </p>

          <button
            type="button"
            className="refresh-button"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw size={16} className={refreshing ? 'spinning' : ''} />
            {refreshing ? t('subscription.checking') : t('subscription.checkAgain')}
          </button>
          {refreshError && <p className="refresh-error">{refreshError}</p>}
        </div>
      </main>
    </div>
  );
}
